import { useCallback, useEffect, useState, useRef, useMemo, memo } from 'react';
import * as Y from 'yjs';
import { TextAreaBinding } from 'y-textarea';
import { generateKeyBetween } from 'fractional-indexing';
import {
  Block,
  BlockType,
  BlockYMap,
  createBlock,
  yMapToBlock,
  updateYMap,
  serializeBlocksToMarkdown,
  MAX_INDENT_LEVEL,
  getBlockYText,
  compareBlockPositions,
  addBlockToYArray,
  getPosition,
} from './blockTypes';

const SHOW_BUTTONS = true;

interface BlockEditorProps {
  yArray: Y.Array<BlockYMap>;
  onTextChanged?: (markdown: string) => void;
  onBlocksChanged?: (blocks: Block[]) => void;
  editable?: boolean;
  onTranslationTrigger?: () => void;
}

interface BlockItemProps {
  blockId: string;
  yArray: Y.Array<BlockYMap>;
  isFocused: boolean;
  isFirst: boolean;
  isLast: boolean;
  editable: boolean;
  onFocus: (blockId: string) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>, block: Block) => void;
  onToggleHeading: (blockId: string) => void;
  onMoveBlock: (blockId: string, direction: 'up' | 'down') => void;
  onIndent: (blockId: string) => void;
  onDedent: (blockId: string) => void;
}

const BlockItem = memo(function BlockItem({
  blockId,
  yArray,
  isFocused,
  isFirst,
  isLast,
  editable,
  onFocus,
  onBlur,
  onKeyDown,
  onToggleHeading,
  onMoveBlock,
  onIndent,
  onDedent,
}: BlockItemProps) {
  const [version, setVersion] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Find the Y.Map for this specific block
  const yMap = useMemo(() => {
    return yArray.toArray().find(map => map.get('id') === blockId);
    // version isn't read here, but must stay a dependency to force re-lookup
    // when the underlying Yjs map mutates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yArray, blockId, version]);

  // Observe only this block's Y.Map
  useEffect(() => {
    if (!yMap) return;

    const observer = () => {
      setVersion(v => v + 1);
    };

    yMap.observeDeep(observer);
    return () => yMap.unobserveDeep(observer);
  }, [yMap]);

  // Auto-resize textarea to fit content
  const autoResize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to get accurate measurement
    textarea.style.height = '0px';
    // Set height to scrollHeight to fit content
    textarea.style.height = textarea.scrollHeight + 'px';
  }, []);

  // Focus management
  useEffect(() => {
    if (isFocused && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.focus();
      // Move cursor to end
      textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
    }
  }, [isFocused]);

  if (!yMap) {
    return null; // Block was deleted
  }

  const block = yMapToBlock(yMap);
  // Headings are not indented; bullets use level for indentation
  const indentPadding = block.type === 'heading' ? 0 : block.level * 12;
  // Headings use level for number of #'s (level 0 = ##, level 1 = ###, etc.)
  const prefix = block.type === 'heading'
    ? '#'.repeat(block.level + 2) + ' '
    : '- ';

  return (
    <div
      className="flex items-start gap-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-900 text-gray-900 dark:text-gray-100"
      style={{ paddingLeft: `${indentPadding}px` }}
    >

      {/* Prefix indicator - clickable to toggle heading (Cmd+H) */}
      <button
        type="button"
        onClick={() => onToggleHeading(blockId)}
        onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking
        className={`shrink-0 bg-transparent border-none cursor-pointer hover:opacity-60 p-0 ${
          block.type === 'heading' ? 'font-bold' : ''
        }`}
        title="Click to toggle heading (Cmd+H)"
        disabled={!editable}
      >
        {prefix}
      </button>

      {/* Content */}
      <div className="grow min-w-0">
        {isFocused && editable ? (
          <textarea
            ref={(el) => {
              if (!el) return;
              textareaRef.current = el;

              const yText = getBlockYText(yMap);
              const binding = new TextAreaBinding(yText, el);

              // Initial resize
              autoResize();
              return () => binding.destroy();
            }}
            defaultValue={block.content}
            onKeyDown={(e) => onKeyDown(e, block)}
            onBlur={onBlur}
            className={`w-full border-none outline-none resize-none ${
              block.type === 'heading' ? 'font-bold text-lg' : ''
            }`}
            rows={1}
            style={{ minHeight: '1.5rem' }}
          />
        ) : (
          <div
            onClick={() => editable && onFocus(blockId)}
            className={`cursor-text ${block.type === 'heading' ? 'font-bold text-lg' : ''}`}
          >
            {block.content || (
              <span className="text-gray-400 dark:text-gray-500 italic">Click to edit...</span>
            )}
          </div>
        )}
      </div>
      {/* Operation buttons - only shown when focused */}
      {SHOW_BUTTONS && editable && isFocused && (
        <div
          className="flex gap-0 pt-1 flex-shrink-0"
          onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking buttons
        >
          <button
            type="button"
            onClick={() => onMoveBlock(blockId, 'up')}
            className="px-1 text-xs border rounded hover:bg-gray-100"
            title="Move up (Cmd+↑)"
            disabled={isFirst}
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMoveBlock(blockId, 'down')}
            className="px-1 text-xs border rounded hover:bg-gray-100"
            title="Move down (Cmd+↓)"
            disabled={isLast}
          >
            ↓
          </button>
          <button
            type="button"
            onClick={() => onIndent(blockId)}
            className="px-1 text-xs border rounded hover:bg-gray-100"
            title="Indent (Tab)"
            disabled={block.level >= MAX_INDENT_LEVEL}
          >
            →
          </button>
          <button
            type="button"
            onClick={() => onDedent(blockId)}
            className="px-1 text-xs border rounded hover:bg-gray-100"
            title="Dedent (Shift+Tab)"
            disabled={block.level === 0}
          >
            ←
          </button>
        </div>
      )}
    </div>
  );
});

export function BlockEditor({ yArray, onTextChanged, onBlocksChanged, editable = true, onTranslationTrigger }: BlockEditorProps) {
  const [version, setVersion] = useState(0);
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(null);
  const onTextChangedRef = useRef(onTextChanged);
  const onBlocksChangedRef = useRef(onBlocksChanged);

  // Keep refs updated
  useEffect(() => {
    onTextChangedRef.current = onTextChanged;
    onBlocksChangedRef.current = onBlocksChanged;
  }, [onTextChanged, onBlocksChanged]);

  // Initialize and observe Yjs array
  useEffect(() => {
    // @ts-expect-error - for debugging
    window.yArray = yArray;

    // Trigger initial render (synchronous setState in effect is intentional here —
    // this runs only on mount/yArray-change to bootstrap the version counter)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVersion(v => v + 1);

    // Call callbacks with initial state
    const blocks = yArray.toArray().map(yMap => yMapToBlock(yMap)).sort(compareBlockPositions);
    if (onBlocksChangedRef.current) {
      onBlocksChangedRef.current(blocks);
    }
    if (onTextChangedRef.current) {
      const markdown = serializeBlocksToMarkdown(blocks);
      onTextChangedRef.current(markdown);
    }

    // Observer just triggers re-renders
    const observer = () => {
      setVersion(v => v + 1);

      // Notify parent of changes
      const blocks = yArray.toArray().map(yMap => yMapToBlock(yMap)).sort(compareBlockPositions);
      if (onBlocksChangedRef.current) {
        onBlocksChangedRef.current(blocks);
      }
      if (onTextChangedRef.current) {
        const markdown = serializeBlocksToMarkdown(blocks);
        onTextChangedRef.current(markdown);
      }
    };

    yArray.observeDeep(observer);
    return () => {
      yArray.unobserveDeep(observer);
    }
  }, [yArray]);

  // Memoized sorted list of block IDs
  // version is used to trigger re-computation when yArray changes
  const sortedBlockIds = useMemo(() => {
    void version
    const blocks = yArray
      .toArray()
      .map(yMap => yMapToBlock(yMap))
      .sort(compareBlockPositions);
    return blocks.map(b => b.id);
  }, [yArray, version]);

  const updateBlock = useCallback(
    (blockId: string, updates: Partial<Block>) => {
      const index = yArray.toArray().findIndex((yMap) => yMap.get('id') === blockId);
      if (index !== -1) {
        const yMap = yArray.get(index);
        updateYMap(yMap, updates);
      }
    },
    [yArray]
  );

  const deleteBlock = useCallback(
    (blockId: string) => {
      // Never delete the last block
      if (yArray.length <= 1) return;

      const sortedBlocks = yArray
        .toArray()
        .map(yMap => yMapToBlock(yMap))
        .sort(compareBlockPositions);

      const sortedIndex = sortedBlocks.findIndex(b => b.id === blockId);
      if (sortedIndex === -1) return;

      // Find the Y.Map in the array and delete it
      const arrayIndex = yArray.toArray().findIndex((yMap) => yMap.get('id') === blockId);
      if (arrayIndex !== -1) {
        yArray.delete(arrayIndex, 1);

        // Focus previous block in sorted order, if available, otherwise next block
        const newFocusIndex = sortedIndex > 0 ? sortedIndex - 1 : 0;
        if (newFocusIndex < sortedBlocks.length) {
          const newFocusBlock = sortedBlocks[newFocusIndex];
          setFocusedBlockId(newFocusBlock.id);
        }
      }
    },
    [yArray]
  );

  const insertBlockAfter = useCallback(
    (blockId: string, content: string, type: BlockType, level: number) => {
      const sortedBlocks = yArray
        .toArray()
        .map(yMap => yMapToBlock(yMap))
        .sort(compareBlockPositions);

      const currentIndex = sortedBlocks.findIndex(b => b.id === blockId);
      if (currentIndex === -1) {
        console.warn('insertBlockAfter: blockId not found', blockId);
        return;
      }

      // Calculate position between current and next block
      const currentPos = sortedBlocks[currentIndex].position;
      const nextPos = currentIndex < sortedBlocks.length - 1
        ? sortedBlocks[currentIndex + 1].position
        : null;
      const newPosition = generateKeyBetween(currentPos, nextPos);

      const newBlock = createBlock(
        content,
        type,
        level,
        newPosition
      );
      addBlockToYArray(yArray, newBlock);
      setFocusedBlockId(newBlock.id);
    },
    [yArray]
  );

  const moveBlock = useCallback(
    (blockId: string, direction: 'up' | 'down') => {
      const sortedBlocks = yArray
        .toArray()
        .map(yMap => yMapToBlock(yMap))
        .sort(compareBlockPositions);

      const currentIndex = sortedBlocks.findIndex(b => b.id === blockId);
      if (currentIndex === -1) return;

      // Calculate new position between neighbors (excluding current block)
      let prevPos: string | null;
      let nextPos: string | null;

      if (direction === 'up') {
        if (currentIndex === 0) return; // Already at top
        // Moving up: position between (currentIndex - 2) and (currentIndex - 1)
        prevPos = currentIndex > 1 ? sortedBlocks[currentIndex - 2].position : null;
        nextPos = sortedBlocks[currentIndex - 1].position;
      } else {
        if (currentIndex === sortedBlocks.length - 1) return; // Already at bottom
        // Moving down: position between (currentIndex + 1) and (currentIndex + 2)
        prevPos = sortedBlocks[currentIndex + 1].position;
        nextPos = currentIndex < sortedBlocks.length - 2 ? sortedBlocks[currentIndex + 2].position : null;
      }

      const newPosition = generateKeyBetween(prevPos, nextPos);

      // Update position (not delete-insert!)
      updateBlock(blockId, { position: newPosition });
    },
    [yArray, updateBlock]
  );

  const indent = useCallback(
    (blockId: string) => {
      const yMap = yArray.toArray().find((map) => map.get('id') === blockId);
      if (yMap) {
        const block = yMapToBlock(yMap);
        if (block.level < MAX_INDENT_LEVEL) {
          updateBlock(blockId, { level: block.level + 1 });
        }
      }
    },
    [yArray, updateBlock]
  );

  const dedent = useCallback(
    (blockId: string) => {
      const yMap = yArray.toArray().find((map) => map.get('id') === blockId);
      if (yMap) {
        const block = yMapToBlock(yMap);
        if (block.level > 0) {
          updateBlock(blockId, { level: block.level - 1 });
        }
      }
    },
    [yArray, updateBlock]
  );

  const toggleHeading = useCallback(
    (blockId: string) => {
      const yMap = yArray.toArray().find((map) => map.get('id') === blockId);
      if (yMap) {
        const block = yMapToBlock(yMap);
        const newType: BlockType = block.type === 'heading' ? 'bullet' : 'heading';
        updateBlock(blockId, { type: newType });
      }
    },
    [yArray, updateBlock]
  );

  const handleClickEmptyArea = useCallback(() => {
    if (!editable) return;

    if (sortedBlockIds.length > 0) {
      // Focus the last block
      setFocusedBlockId(sortedBlockIds[sortedBlockIds.length - 1]);
    } else {
      // Create a new block and focus it
      const newBlock = createBlock('', 'bullet', 0, getPosition(null, null));
      addBlockToYArray(yArray, newBlock);
      setFocusedBlockId(newBlock.id);
    }
  }, [editable, sortedBlockIds, yArray]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>, block: Block) => {
      if (!editable) return;

      const textarea = e.currentTarget;
      const cursorPos = textarea.selectionStart;
      const isAtStart = cursorPos === 0;
      const isAtEnd = cursorPos === textarea.value.length;

      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (onTranslationTrigger) {
          onTranslationTrigger();
        }
      }
      // Enter: split block or create new block
      else if (e.key === 'Enter') {
        e.preventDefault();
        const beforeCursor = block.content.substring(0, cursorPos);
        const afterCursor = block.content.substring(cursorPos);

        const performSplit = () => {
          // Update current block with content before cursor
          updateBlock(block.id, { content: beforeCursor });

          // Create new block with content after cursor
          const newType = block.type === 'heading' ? 'bullet' : block.type;
          const newLevel = block.type === 'heading' ? 0 : block.level;

          insertBlockAfter(block.id, afterCursor, newType, newLevel);
        };

        // Wrap both operations in a transaction for atomicity
        if (yArray.doc) {
          yArray.doc.transact(performSplit);
        } else {
          console.warn('BlockEditor: yArray not attached to document, split operation will not be atomic');
          performSplit();
        }

        if (onTranslationTrigger) {
          onTranslationTrigger();
        }
      }
      // Backspace at start: demote (decrement level, or convert heading to bullet at level 0)
      else if (e.key === 'Backspace' && isAtStart) {
        if (block.level > 0) {
          e.preventDefault();
          dedent(block.id);
        } else if (block.type === 'heading') {
          e.preventDefault();
          updateBlock(block.id, { type: 'bullet' });
        } else if (block.content === '') {
          // Only delete empty blocks at lowest level
          e.preventDefault();
          deleteBlock(block.id);
        }
        // Otherwise, let default backspace behavior happen (e.g., delete char before cursor)
      }
      // # at start: promote to heading or increment heading level
      else if (e.key === '#' && isAtStart && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        if (block.type === 'bullet') {
          // Convert bullet to heading at level 0
          updateBlock(block.id, { type: 'heading', level: 0 });
        } else if (block.type === 'heading' && block.level < MAX_INDENT_LEVEL) {
          // Increment heading level
          updateBlock(block.id, { level: block.level + 1 });
        }
        // At max level, do nothing (ignore the #)
      }
      // Tab: indent
      else if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        indent(block.id);
      }
      // Shift+Tab: dedent
      else if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        dedent(block.id);
      }
      // Cmd/Ctrl+H: toggle heading
      else if (e.key === 'h' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleHeading(block.id);
      }
      // Cmd/Ctrl+Up: move up
      else if (e.key === 'ArrowUp' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        moveBlock(block.id, 'up');
      }
      // Cmd/Ctrl+Down: move down
      else if (e.key === 'ArrowDown' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        moveBlock(block.id, 'down');
      }
      // ArrowUp at start: focus previous block
      else if (e.key === 'ArrowUp' && isAtStart) {
        e.preventDefault();
        const index = sortedBlockIds.findIndex((id) => id === block.id);
        if (index > 0) {
          setFocusedBlockId(sortedBlockIds[index - 1]);
        }
      }
      // ArrowDown at end: focus next block
      else if (e.key === 'ArrowDown' && isAtEnd) {
        e.preventDefault();
        const index = sortedBlockIds.findIndex((id) => id === block.id);
        if (index < sortedBlockIds.length - 1) {
          setFocusedBlockId(sortedBlockIds[index + 1]);
        }
      }
    },
    [editable, sortedBlockIds, updateBlock, insertBlockAfter, deleteBlock, indent, dedent, toggleHeading, moveBlock, onTranslationTrigger, yArray.doc]
  );

  return (
    <div className="border rounded p-1 bg-white dark:bg-gray-800">
      {sortedBlockIds.length === 0 ? (
        <div
          className="text-gray-400 dark:text-gray-500 italic cursor-text min-h-[2rem] flex items-center"
          onClick={handleClickEmptyArea}
        >
          {editable ? 'Click to start writing...' : 'No blocks yet'}
        </div>
      ) : (
        <>
          {sortedBlockIds.map((blockId, index) => (
            <BlockItem
              key={blockId}
              blockId={blockId}
              yArray={yArray}
              isFocused={focusedBlockId === blockId}
              isFirst={index === 0}
              isLast={index === sortedBlockIds.length - 1}
              editable={editable}
              onFocus={setFocusedBlockId}
              onBlur={() => setFocusedBlockId(null)}
              onKeyDown={handleKeyDown}
              onToggleHeading={toggleHeading}
              onMoveBlock={moveBlock}
              onIndent={indent}
              onDedent={dedent}
            />
          ))}
          {editable && (
            <div
              className="min-h-[2rem] cursor-text"
              onClick={handleClickEmptyArea}
            />
          )}
        </>
      )}
    </div>
  );
}
